import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Upload, ImageOff, Save, X, Plus, ClipboardList, Trash2, GripVertical, Link as LinkIcon, Search, Sparkles, Barcode, Copy, Images } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { useLanguage } from "@/context/LanguageContext";
import { OrderProductImportButton, toPriceRowCarton, toPriceRowCost } from "@/modules/order-products";
import { useAuth } from "@/context/AuthContext";
import { ProcurementHistoryDialog } from "@/components/products/ProcurementHistoryDialog";
import UpcLookupDialog from "@/components/quotes/UpcLookupDialog";

const IMG_BASE = process.env.REACT_APP_BACKEND_URL || "";

/** ITEM_EDITABLE_FIELDS on backend — kept in sync so we only send what backend accepts. */
const ITEM_FIELDS = ["description", "gt_description", "specification", "material", "quote_photo_url",
                     "buyer_item_number", "buyer", "procurement_note", "moq", "inquire", "stock_quantity", "compression_rate"];
/** ROW_NUMERIC_FIELDS + size — the row-level attributes the user can tweak per size. */
const ROW_NUM_FIELDS = ["final_price", "factory_cost", "factory_cost_usd", "case_length",
                        "case_width", "case_height", "case_weight", "qty_per_ctn",
                        "container_qty_40"];

/**
 * Per-quote product-detail editor.
 * - Edits ONLY the quote's snapshot. Never touches the source Product / PriceList.
 * - Prev/Next buttons walk through the quote's items list.
 * - Auto-saves on close AND when moving to another item.
 * - Dirty state is kept locally; a single PUT (+ N row PUTs) is fired only when needed.
 */
export default function QuoteItemEditorDialog({ open, onClose, quoteId, items, startIndex, onSaved, readOnly, quoteCurrency = "USD" }) {
  const isCad = (quoteCurrency || "").toUpperCase() === "CAD";
  const { t } = useLanguage();
  const { user } = useAuth();
  const [index, setIndex] = useState(startIndex || 0);
  const [draft, setDraft] = useState(null);           // local copy of current item
  const [dirty, setDirty] = useState({ item: false, rows: new Set() });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  // 2026-02-09 · State for the two new toolbar buttons per user spec:
  //   - 采购报价记录 (procurement history) opens the shared dialog scoped to
  //     the current buyer item number and imports selected rows AS quote
  //     snapshots only (never mutates the source product).
  //   - 添加尺寸 appends a blank price row to this quote item.
  const [procOpen, setProcOpen] = useState(false);
  const [writingBack, setWritingBack] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [upcOpen, setUpcOpen] = useState(false);   // 2026-06 · UPC 查询
  // 2026-02-27 · Buyer dropdown options (系统设置里的采购员列表). Fetched
  // once when the dialog opens; falls back to empty on error.
  const [buyerOptions, setBuyerOptions] = useState([]);
  useEffect(() => {
    if (!open) return;
    let alive = true;
    api.get("/products/meta/options")
      .then(({ data }) => { if (alive) setBuyerOptions(data?.buyers || []); })
      .catch(() => { if (alive) setBuyerOptions([]); });
    return () => { alive = false; };
  }, [open]);
  const [appending, setAppending] = useState(false);
  // 2026-06 · 产品一览表相似图片 — thumbnails of overview records whose item
  // number looks like this product's; clicking one replaces the quote photo.
  const [similarPhotos, setSimilarPhotos] = useState([]);
  // 2026-06 · Free-text search over the WHOLE 产品一览表 — when the box has
  // text we show those results instead of the auto-matched similar ones.
  const [photoQuery, setPhotoQuery] = useState("");
  const [photoPickerOpen, setPhotoPickerOpen] = useState(false);   // 2026-06-25 · 相似图片弹窗
  const [photoResults, setPhotoResults] = useState([]);
  const [photoSearching, setPhotoSearching] = useState(false);
  const [cutoutBusy, setCutoutBusy] = useState(false);
  const handleRemoveBg = async () => {
    const item = items?.[index];
    if (!item?.item_number) return;
    setCutoutBusy(true);
    try {
      const { data } = await api.post(`/quotes/${quoteId}/items/${encodeURIComponent(item.item_number)}/remove-bg`);
      // 2026-06-26 · 必须走 setItemField（标脏）—— 否则父级 items 仍是旧图，
      // 之后任何一次自动保存都会把旧的 quote_photo_url 写回，抠图结果丢失。
      setItemField("quote_photo_url", data.quote_photo_url);
      toast.success(t("products.bgRemoved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setCutoutBusy(false);
    }
  };
  useEffect(() => {
    const item = items?.[index];
    if (!open || !item?.item_number) { setSimilarPhotos([]); return; }
    let alive = true;
    api.get("/photo-search", { params: { item_number: item.source_item_number || item.item_number, per_source: 6 } })
      .then(({ data }) => { if (alive) setSimilarPhotos(data?.items || []); })
      .catch(() => { if (alive) setSimilarPhotos([]); });
    return () => { alive = false; };
  }, [open, index, items]);
  useEffect(() => {
    const q = photoQuery.trim();
    if (!open || q.length < 2) { setPhotoResults([]); setPhotoSearching(false); return; }
    let alive = true;
    setPhotoSearching(true);
    const timer = setTimeout(() => {
      api.get("/photo-search", { params: { search: q, per_source: 8 } })
        .then(({ data }) => { if (alive) setPhotoResults(data?.items || []); })
        .catch(() => { if (alive) setPhotoResults([]); })
        .finally(() => { if (alive) setPhotoSearching(false); });
    }, 350);
    return () => { alive = false; clearTimeout(timer); };
  }, [open, photoQuery]);
  // 2026-06 · 生成链接 (采购报价要求) — creates a 30-day link scoped to this item's 采购员.
  const [linkBusy, setLinkBusy] = useState(false);
  const [itemNumberInput, setItemNumberInput] = useState("");   // 2026-06 · 报价单内可改产品编号
  const [linkInfo, setLinkInfo] = useState(null); // { url, buyer, expires_at, item_count }
  // Drag & drop reorder state for the size (price) rows.
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const overIdRef = useRef(null);
  const originalRef = useRef(null);                   // snapshot at time of load — used for diff
  // Item-numbers we've already tried auto-generating a GT description for during this dialog
  // session. Prevents duplicate LLM calls when the user pages Prev/Next and comes back.
  const gtAutoTriedRef = useRef(new Set());
  // 2026-09 · 自动重算「计算价」—— 任何参数改动停下来后就保存并拉回后端算好的价格。
  const recalcRef = useRef(null);
  const recalcBusyRef = useRef(false);

  // Load draft whenever we open OR the index/items array changes.
  useEffect(() => {
    if (!open) return;
    // 2026-02-09 · Bounds-check the index. A stale ``startIndex`` (e.g. items
    // shifted after a removeItem) used to leave ``it`` undefined and the
    // effect returned early, leaving ``draft`` at null → the whole component
    // rendered nothing (blank overlay reported in production). Now we clamp
    // and always fall back to the first item.
    const list = items || [];
    if (list.length === 0) return;                   // truly empty → early return, guarded UI below shows "no item"
    const safeIdx = Math.min(Math.max(0, index), list.length - 1);
    if (safeIdx !== index) { setIndex(safeIdx); return; }
    const it = list[safeIdx];
    if (!it) return;
    const clone = JSON.parse(JSON.stringify(it));
    // Ensure price_rows is always an array so downstream .map never explodes.
    if (!Array.isArray(clone.price_rows)) clone.price_rows = [];
    // Assign a synthetic id to any legacy row missing one so React keys +
    // per-row updates stay stable during editing.
    clone.price_rows = clone.price_rows.map((r, i) => (
      r && r.id ? r : { ...(r || {}), id: r?.id || `row-${i}-${Math.random().toString(36).slice(2, 8)}` }
    ));
    setDraft(clone);
    setItemNumberInput(clone.item_number || "");
    originalRef.current = clone;
    setDirty({ item: false, rows: new Set() });
    // Auto-generate GT description on open if empty (per user spec 2026-02).
    if (!readOnly && !clone.gt_description && (clone.description || "").trim().length > 0) {
      const key = clone.item_number || String(safeIdx);
      if (!gtAutoTriedRef.current.has(key)) {
        gtAutoTriedRef.current.add(key);
        // Fire-and-forget; the input will populate when the response arrives.
        autoGenerateGt(clone).catch(() => {});
      }
    }
  }, [open, index, items]);

  // Clear the auto-gen tracker whenever the dialog is closed so a fresh open re-attempts.
  useEffect(() => { if (!open) gtAutoTriedRef.current = new Set(); }, [open]);

  // Reset to caller-provided startIndex whenever it changes (opening on a different row).
  useEffect(() => { if (open) setIndex(startIndex || 0); }, [open, startIndex]);

  // 2026-09 · 任何参数（关税 / 工厂成本 / 压缩率 / 装箱数 …）改动后，停止输入
  // 900ms 就自动保存并把后端重新计算的「计算价」拉回来 —— 用户要求价格随参数
  // 立即变化，而不是只有压缩率才触发重算。
  useEffect(() => {
    if (!open || readOnly) return;
    if (!dirty.item && dirty.rows.size === 0) return;
    const tm = setTimeout(() => { recalcRef.current?.(); }, 900);
    return () => clearTimeout(tm);
  }, [open, readOnly, draft, dirty]);

  if (!open) return null;

  const total = items?.length || 0;
  const canPrev = index > 0;
  const canNext = index < total - 1;

  // 2026-02-09 · If we're open but the draft hasn't been hydrated yet (empty
  // items array, race, or malformed production data) render a graceful
  // fallback instead of returning ``null`` — the old null-return produced a
  // completely blank overlay in production when a quote item was missing
  // fields the effect needed. The user can now close the dialog and see
  // exactly what happened.
  if (!draft) {
    return (
      <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
        <DialogContent className="max-w-md" data-testid="quote-item-editor-dialog-empty">
          <DialogHeader>
            <DialogTitle className="text-sm font-medium">{t("quote.editor.title")}</DialogTitle>
          </DialogHeader>
          <div className="py-8 text-center text-sm text-muted-foreground" data-testid="quote-editor-empty-hint">
            {total === 0
              ? (t("quote.editor.noItems") || "报价单里还没有产品，请先添加产品再编辑。")
              : (t("quote.editor.loading") || "加载中……如长时间未显示，请关闭后重新点击。")}
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/40">
            <Button variant="outline" onClick={() => onClose?.()} data-testid="quote-editor-empty-close"
              className="rounded-md gap-1">
              <X size={14} /> {t("common.close") || "关闭"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ---- Field editors ----
  const setItemField = (key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setDirty((prev) => ({ ...prev, item: true }));
  };

  // 2026-06 · 箱规/每箱装箱数一改就重算 40尺柜装箱量（用户规格）：
  //   40尺柜装箱量 = 65,000,000 ÷ 箱长 ÷ 箱宽 ÷ 箱高 × 每箱装箱数（箱规单位 cm）
  // 只在报价单产品编辑窗体生效；手填的柜量会被覆盖。
  const QTY40_TRIGGERS = ["case_length", "case_width", "case_height", "qty_per_ctn"];
  const recomputeQty40 = (row) => {
    const l = Number(row.case_length) || 0;
    const w = Number(row.case_width) || 0;
    const h = Number(row.case_height) || 0;
    const per = Number(row.qty_per_ctn) || 0;
    if (l <= 0 || w <= 0 || h <= 0 || per <= 0) return row;
    return { ...row, container_qty_40: Math.round((65_000_000 / l / w / h) * per) };
  };

  const setRowField = (rowId, key, value) => {
    setDraft((d) => ({
      ...d,
      price_rows: (d.price_rows || []).map((r) => {
        if (r.id !== rowId) return r;
        const next = { ...r, [key]: value };
        return QTY40_TRIGGERS.includes(key) ? recomputeQty40(next) : next;
      }),
    }));
    setDirty((prev) => {
      const rows = new Set(prev.rows);
      rows.add(rowId);
      return { ...prev, rows };
    });
  };

  /**
   * 2026-02-09 · Delete a single price row from this quote item snapshot.
   * Uses the existing per-row DELETE endpoint. If it was the LAST row of the
   * item. 2026-06 · The item is always kept (even with zero rows) so the
   * dialog simply refreshes with an empty price table.
   */
  const removeRow = async (rowId) => {
    if (!draft?.item_number || readOnly || appending) return;
    if (!window.confirm(t("quote.editor.confirmDeleteRow") || "确认删除这一尺寸行？")) return;
    // Persist pending edits first so no in-memory changes are lost.
    try { await persistDraft(); } catch { return; }
    setAppending(true);
    try {
      const { data } = await api.delete(`/quotes/${quoteId}/items/${draft.item_number}/rows/${rowId}`);
      if (onSaved) onSaved(data);
      const newItem = (data?.items || []).find((it) => it.item_number === draft.item_number);
      if (newItem) {
        const clone = JSON.parse(JSON.stringify(newItem));
        clone.price_rows = (clone.price_rows || []).map((r, i) => (r.id ? r : { ...r, id: `row-${i}-${Math.random().toString(36).slice(2, 8)}` }));
        setDraft(clone);
        originalRef.current = clone;
        setDirty({ item: false, rows: new Set() });
      } else {
        // Item itself was deleted (last row) — close the dialog gracefully.
        onClose?.();
      }
      toast.success(t("quote.editor.rowDeleted") || "已删除");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("quote.editor.saveFailed"));
    } finally {
      setAppending(false);
    }
  };

  /**
   * 2026-02-09 · Append a fresh size row (blank fields) to the current quote
   * item. Backend generates the id if none is supplied; we then merge the
   * server-returned item into the caller so the new row shows up immediately.
   */
  const addBlankRow = async () => {
    if (!draft?.item_number || readOnly || appending) return;
    // Save any pending edits first so we don't lose them when the server
    // replaces our items array with the enriched response.
    try { await persistDraft(); } catch { return; }
    setAppending(true);
    try {
      const { data } = await api.post(`/quotes/${quoteId}/items/${draft.item_number}/rows`, {
        size: "", upc: "", cost_currency: draft.price_rows?.[0]?.cost_currency || "",
      });
      if (onSaved) onSaved(data);
      // Move our local view to the newly-added row (last one).
      const newItem = (data?.items || []).find((it) => it.item_number === draft.item_number);
      if (newItem) {
        const clone = JSON.parse(JSON.stringify(newItem));
        clone.price_rows = (clone.price_rows || []).map((r, i) => (r.id ? r : { ...r, id: `row-${i}-${Math.random().toString(36).slice(2, 8)}` }));
        setDraft(clone);
        originalRef.current = clone;
        setDirty({ item: false, rows: new Set() });
      }
      toast.success(t("quote.editor.rowAdded") || "已添加尺寸");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("quote.editor.saveFailed"));
    } finally {
      setAppending(false);
    }
  };

  /**
   * 2026-02-09 · Import selected procurement-quote rows AS this quote item's
   * price rows (snapshot-only, does NOT touch the source product). Uses the
   * same ``toPriceRow`` shape as ProductForm to stay consistent with the
   * product-side importer.
   */
  const importProcurementRows = async (procRows) => {
    if (!Array.isArray(procRows) || procRows.length === 0 || readOnly) return;
    try { await persistDraft(); } catch { return; }
    setAppending(true);
    try {
      let latest = null;
      for (const row of procRows) {
        // Server ignores unknown fields; our POST helper coerces numerics.
        const payload = {
          size: row.size || "",
          upc: row.upc || "",
          cost_currency: row.cost_currency || "",
          factory_cost: row.factory_cost ?? row.price ?? 0,
          case_length: row.case_length ?? row.length ?? 0,
          case_width: row.case_width ?? row.width ?? 0,
          case_height: row.case_height ?? row.height ?? 0,
          case_weight: row.case_weight ?? row.weight ?? 0,
          qty_per_ctn: row.qty_per_ctn ?? row.qty ?? 0,
          container_qty_40: row.container_qty_40 ?? 0,
          source_item_number: row.source_item_number || "",
          source_quote_id: row.source_quote_id || "",
          // 2026-09 · 导入人（登录用户）+ 导入日期，标注在报价单的价格行上。
          source_buyer: row.source_buyer || "",
          imported_at: row.imported_at || "",
        };
        const { data } = await api.post(`/quotes/${quoteId}/items/${draft.item_number}/rows`, payload);
        latest = data;
      }
      if (latest) {
        if (onSaved) onSaved(latest);
        const newItem = (latest.items || []).find((it) => it.item_number === draft.item_number);
        if (newItem) {
          const clone = JSON.parse(JSON.stringify(newItem));
          clone.price_rows = (clone.price_rows || []).map((r, i) => (r.id ? r : { ...r, id: `row-${i}-${Math.random().toString(36).slice(2, 8)}` }));
          setDraft(clone);
          originalRef.current = clone;
          setDirty({ item: false, rows: new Set() });
        }
      }
      toast.success(t("quote.editor.rowsImported", { n: procRows.length }) || `已导入 ${procRows.length} 条采购报价`);
      setProcOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("quote.editor.saveFailed"));
    } finally {
      setAppending(false);
    }
  };

  /**
   * Drag & drop reordering of the size (price) rows. Local order updates
   * immediately for feedback, then persisted via POST /rows/reorder.
   */
  const startRowDrag = (rowId) => (e) => {
    if (readOnly) return;
    e.preventDefault();
    setDragId(rowId);
    overIdRef.current = null;
    const move = (ev) => {
      const trs = Array.from(document.querySelectorAll("tr[data-editor-row-id]"));
      const hit = trs.find((tr) => {
        const b = tr.getBoundingClientRect();
        return ev.clientY >= b.top && ev.clientY <= b.bottom;
      });
      if (hit?.dataset.editorRowId) {
        overIdRef.current = hit.dataset.editorRowId;
        setOverId(hit.dataset.editorRowId);
      }
    };
    const up = () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
      const target = overIdRef.current;
      setDragId(null); setOverId(null);
      if (target) commitReorder(rowId, target);
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
  };

  /**
   * 2026-06 · 生成链接 — a 30-day link the 采购 opens (login required, 采购 role)
   * to read this quote's products assigned to them plus 采购报价要求, and file
   * their 采购报价 back into the system.
   */
  const generateProcurementLink = async () => {
    if (!draft?.buyer) {
      toast.error(t("quote.editor.linkNeedsBuyer"));
      return;
    }
    try { await persistDraft(); } catch { return; }
    setLinkBusy(true);
    try {
      const { data } = await api.post(`/quotes/${quoteId}/procurement-link`, { buyer: draft.buyer });
      setLinkInfo(data);
      try { await navigator.clipboard.writeText(data.url); } catch { /* clipboard blocked — user can copy manually */ }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("quote.editor.saveFailed"));
    } finally {
      setLinkBusy(false);
    }
  };

  /** 2026-09 · 保存当前改动并把后端重算后的价格行（计算价 / 压缩后柜量）拉回
   *  来，让「计算价」随任何参数改动立即更新。 */
  const recalcNow = async () => {
    if (readOnly || recalcBusyRef.current) return;
    if (!dirty.item && dirty.rows.size === 0) return;
    recalcBusyRef.current = true;
    let updated = null;
    try { updated = await persistDraft(); } catch { return; } finally { recalcBusyRef.current = false; }
    const newItem = (updated?.items || []).find((it) => it.item_number === draft.item_number);
    if (!newItem) return;
    const clone = JSON.parse(JSON.stringify(newItem));
    clone.price_rows = (clone.price_rows || []).map((r, i) => (r.id ? r : { ...r, id: `row-${i}` }));
    setDraft(clone);
    originalRef.current = clone;
    setDirty({ item: false, rows: new Set() });
  };
  recalcRef.current = recalcNow;

  /** 2026-09 · 复制 Main Product — duplicate this item (price rows included)
   *  inside THIS quote only; no product is created in the product library. */
  const duplicateItem = async () => {
    setDuplicating(true);
    try {
      if (dirty.item || dirty.rows.size) await persistDraft();
      const { data } = await api.post(`/quotes/${quoteId}/items/${encodeURIComponent(draft.item_number)}/duplicate`);
      onSaved?.(data);
      const newIdx = (data.items || []).findIndex((it) => it.item_number.startsWith(`${draft.item_number}-`));
      toast.success(t("quote.editor.duplicated"));
      if (newIdx >= 0) setIndex(newIdx);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDuplicating(false);
    }
  };

  /** 2026-06 · 报价单内修改产品编号（外销/销售主管/管理员）—— 只改本报价单，产品库不动。 */
  const commitItemNumber = async (value) => {
    const next = String(value || "").trim();
    if (readOnly || !next || next === draft.item_number) {
      setItemNumberInput(draft.item_number || "");
      return;
    }
    try {
      if (dirty.item || dirty.rows.size) await persistDraft();
      const { data } = await api.patch(
        `/quotes/${quoteId}/items/${encodeURIComponent(draft.item_number)}/item-number`,
        { item_number: next },
      );
      onSaved?.(data);
      const newItem = (data?.items || []).find((it) => it.item_number === next);
      if (newItem) {
        const clone = JSON.parse(JSON.stringify(newItem));
        clone.price_rows = (clone.price_rows || []).map((r, i) => (r.id ? r : { ...r, id: `row-${i}` }));
        setDraft(clone);
        originalRef.current = clone;
        setDirty({ item: false, rows: new Set() });
      }
      toast.success(t("quote.editor.itemNumberSaved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setItemNumberInput(draft.item_number || "");
    }
  };

  /** 2026-09 · 写回产品资料 — 把本报价单里该产品的价格行（不含 Final Price）
   *  连同压缩率、关税写回产品库；同名尺寸更新，新尺寸新增，产品库多出的行不动。 */
  const writeBackToProduct = async () => {
    if (!window.confirm(t("quote.editor.writeBackConfirm"))) return;
    setWritingBack(true);
    try {
      if (dirty.item || dirty.rows.size) await persistDraft();
      const { data } = await api.post(`/quotes/${quoteId}/items/${encodeURIComponent(draft.item_number)}/write-back`);
      toast.success(t("quote.editor.writeBackDone")
        .replace("{updated}", data.updated).replace("{created}", data.created));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setWritingBack(false);
    }
  };

  const commitReorder = async (fromId, toId) => {    if (readOnly || !fromId || !toId || fromId === toId) return;
    const rows = [...(draft.price_rows || [])];
    const from = rows.findIndex((r) => r.id === fromId);
    const to = rows.findIndex((r) => r.id === toId);
    if (from < 0 || to < 0) return;
    const [moved] = rows.splice(from, 1);
    rows.splice(to, 0, moved);
    setDraft((d) => ({ ...d, price_rows: rows }));
    try { await persistDraft(); } catch { return; }
    try {
      const { data } = await api.post(
        `/quotes/${quoteId}/items/${draft.item_number}/rows/reorder`,
        { order: rows.map((r) => r.id) },
      );
      if (onSaved) onSaved(data);
      const newItem = (data?.items || []).find((it) => it.item_number === draft.item_number);
      if (newItem) {
        const clone = JSON.parse(JSON.stringify(newItem));
        clone.price_rows = (clone.price_rows || []).map((r, i) => (r.id ? r : { ...r, id: `row-${i}` }));
        setDraft(clone);
        originalRef.current = clone;
        setDirty({ item: false, rows: new Set() });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("quote.editor.saveFailed"));
    }
  };

  // ---- Persist current draft (fire only for actually-changed sub-resources) ----
  const persistDraft = async () => {
    if (!dirty.item && dirty.rows.size === 0) return null;
    setSaving(true);
    let updated = null;
    try {
      if (dirty.item) {
        const payload = {};
        ITEM_FIELDS.forEach((k) => { payload[k] = draft[k] ?? ""; });
        // 2026-06 · 关税 override (CAD quotes) — "" clears it back to the product.
        payload.duty_override = draft.duty_override ?? "";
        const { data } = await api.put(`/quotes/${quoteId}/items/${draft.item_number}`, payload);
        updated = data;
      }
      for (const rowId of dirty.rows) {
        const row = (draft.price_rows || []).find((r) => r.id === rowId);
        if (!row) continue;
        const payload = { size: row.size ?? "", upc: row.upc ?? "", cost_currency: row.cost_currency ?? "" };
        ROW_NUM_FIELDS.forEach((k) => {
          const n = Number(row[k]);
          payload[k] = Number.isFinite(n) ? n : 0;
        });
        const { data } = await api.put(`/quotes/${quoteId}/items/${draft.item_number}/rows/${rowId}`, payload);
        updated = data;
      }
      setDirty({ item: false, rows: new Set() });
      if (updated && onSaved) onSaved(updated);
      return updated;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("quote.editor.saveFailed"));
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const navigate = async (direction) => {
    try { await persistDraft(); } catch { return; }
    setIndex((i) => Math.min(Math.max(0, i + direction), total - 1));
  };

  const handleClose = async () => {
    try { await persistDraft(); } catch { /* keep the dialog open so the user can retry */ return; }
    onClose?.();
  };

  // ---- Photo upload — writes into quote_photo_url, doesn't touch the Product ----
  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/products/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      setItemField("quote_photo_url", data.url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("quote.editor.uploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  // ---- AI auto-generate GT description (target ≤30 chars) from full desc + spec/material ----
  // Result is loaded into the input as a suggestion — user is free to tweak it before saving.
  // ``autoGenerateGt`` is fired silently on dialog open when gt_description is empty (per user
  // spec 2026-02) — it uses the freshly-loaded item snapshot (not the local ``draft`` state
  // which may not be committed yet). It also marks the item as dirty so the generated value
  // is persisted on save/close without user interaction.
  async function autoGenerateGt(item) {
    if (!item) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/generate-gt-description", {
        description: item.description || "",
        specification: item.specification || "",
        material: item.material || "",
      }, { timeout: 60000 });
      if (data.gt_description) {
        // Only fill if the user hasn't already typed something while we were awaiting,
        // AND the current draft is still the same item we generated for (guard against
        // the user paging Prev/Next while the LLM call was in flight).
        setDraft((d) => {
          if (!d) return d;
          if (d.item_number !== item.item_number) return d;
          if ((d.gt_description || "").trim().length > 0) return d;
          return { ...d, gt_description: data.gt_description };
        });
        setDirty((prev) => ({ ...prev, item: true }));
      }
    } catch (err) {
      // AI is best-effort; the user can still type manually. Log so ops can
      // spot LLM-quota / network failures via the browser console.
      console.warn("[quote-item AI translate] failed:", err);
    } finally {
      setAiBusy(false);
    }
  };

  const activePhoto = draft.quote_photo_url || draft.photo_url;
  const isOverride = !!draft.quote_photo_url && draft.quote_photo_url !== draft.photo_url;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent
        className="max-w-[1280px] w-[97vw] max-h-[96vh] overflow-y-auto p-3"
        data-testid="quote-item-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 pr-7">
            <div className="flex items-center gap-2">
              <span className="text-sm font-mono text-brand" data-testid="quote-editor-item-number">{draft.item_number}</span>
              <span className="text-xs text-muted-foreground">· {t("quote.editor.title")}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm"
                disabled={!canPrev || saving}
                onClick={() => navigate(-1)}
                data-testid="quote-editor-prev"
                className="h-7 px-2 rounded-md gap-1 text-xs"
              >
                <ChevronLeft size={13} /> {t("quote.editor.prev")}
              </Button>
              <span className="text-xs font-mono text-muted-foreground min-w-[52px] text-center"
                data-testid="quote-editor-position">
                {index + 1} / {total}
              </span>
              <Button
                variant="outline" size="sm"
                disabled={!canNext || saving}
                onClick={() => navigate(+1)}
                data-testid="quote-editor-next"
                className="h-7 px-2 rounded-md gap-1 text-xs"
              >
                {t("quote.editor.next")} <ChevronRight size={13} />
              </Button>
            </div>
          </DialogTitle>
          <p className="text-[11px] text-muted-foreground pt-1">
            {(dirty.item || dirty.rows.size > 0) ? (
              <span className="text-amber-600 dark:text-amber-400 font-medium" data-testid="quote-editor-dirty-hint">
                {t("quote.editor.autoSaveHint")}
              </span>
            ) : (
              <span data-testid="quote-editor-clean-hint">{t("quote.editor.snapshotHint")}</span>
            )}
          </p>
        </DialogHeader>

        {/* Photo section */}
        <section className="grid grid-cols-1 md:grid-cols-[120px_1fr] gap-3 pt-1">
          <div className="space-y-2">
            <Label className="text-xs">{t("quote.editor.photo")}</Label>
            <div className="w-[120px] h-[120px] rounded-md border border-border overflow-hidden bg-secondary/20 flex items-center justify-center relative">
              {activePhoto ? (
                <img
                  src={`${IMG_BASE}${activePhoto}`}
                  alt="" className="w-full h-full object-contain"
                  data-testid="quote-editor-photo-preview"
                />
              ) : (
                <ImageOff size={32} className="text-muted-foreground" />
              )}
              {isOverride && (
                <span className="absolute top-1 right-1 text-[9px] uppercase px-1.5 py-0.5 rounded bg-amber-500/90 text-white font-semibold">
                  {t("quote.editor.override")}
                </span>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className={`inline-flex items-center justify-center gap-1.5 text-xs h-8 px-3 rounded-md border border-border cursor-pointer hover:bg-secondary transition ${uploading || readOnly ? "opacity-50 pointer-events-none" : ""}`}
                data-testid="quote-editor-upload-label">
                <Upload size={12} /> {uploading ? "…" : t("quote.editor.uploadPhoto")}
                <input type="file" accept="image/*" className="hidden" disabled={readOnly}
                  onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
                  data-testid="quote-editor-photo-input" />
              </label>
              {/* 2026-06 · AI 抠图 for this quote's photo (rembg, white bg). */}
              <Button size="sm" variant="outline" className="h-8 text-xs"
                disabled={readOnly || cutoutBusy}
                onClick={handleRemoveBg}
                data-testid="quote-editor-remove-bg">
                <Sparkles size={12} /> {cutoutBusy ? t("products.bgRemoving") : t("products.removeBg")}
              </Button>
              {isOverride && (
                <Button size="sm" variant="ghost" className="h-7 text-[11px] text-muted-foreground"
                  disabled={readOnly}
                  onClick={() => setItemField("quote_photo_url", "")}
                  data-testid="quote-editor-photo-reset">
                  <X size={11} /> {t("quote.editor.resetPhoto")}
                </Button>
              )}
            </div>
            {/* 2026-06 · 产品一览表 photos —— 收成一个按钮，点开弹窗后图片按
                120px 大图展示（与上方产品图片同尺寸，用户要求 2026-06-25）。 */}
            <Button size="sm" variant="outline" className="h-8 text-xs w-full"
              onClick={() => setPhotoPickerOpen(true)}
              data-testid="quote-editor-similar-photos-button">
              <Images size={13} /> {t("quote.editor.photoLibraryShort")} ({(photoQuery.trim().length >= 2 ? photoResults : similarPhotos).length})
            </Button>
          </div>

          {/* 相似图片选择弹窗 */}
          <Dialog open={photoPickerOpen} onOpenChange={setPhotoPickerOpen}>
            <DialogContent className="max-w-[760px]" data-testid="quote-editor-similar-photos-dialog">
              <DialogHeader>
                <DialogTitle className="text-sm font-medium">{t("quote.editor.photoLibrary")}</DialogTitle>
              </DialogHeader>
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={photoQuery}
                  onChange={(e) => setPhotoQuery(e.target.value)}
                  placeholder={t("quote.editor.searchAllPhotos")}
                  className="h-9 text-sm pl-8"
                  data-testid="quote-editor-photo-search"
                />
              </div>
              {(() => {
                const q = photoQuery.trim();
                const list = q.length >= 2 ? photoResults : similarPhotos;
                if (q.length >= 2 && photoSearching && list.length === 0) {
                  return <p className="text-xs text-muted-foreground py-6 text-center">{t("common.loading")}</p>;
                }
                if (list.length === 0) {
                  return <p className="text-xs text-muted-foreground py-6 text-center">{t("quote.editor.noOverviewPhotos")}</p>;
                }
                return (
                  <div className="grid grid-cols-5 gap-2 max-h-[60vh] overflow-y-auto pr-1">
                    {list.map((sp) => {
                      const picked = draft.quote_photo_url === sp.photo_url;
                      return (
                        <button
                          key={sp.id}
                          type="button"
                          disabled={readOnly}
                          onClick={() => { setItemField("quote_photo_url", sp.photo_url); setPhotoPickerOpen(false); }}
                          title={`${sp.item_number || sp.description} · ${t(`quote.editor.src.${sp.source}`)}`}
                          data-testid={`quote-editor-similar-photo-${sp.item_number}`}
                          className={`relative w-full h-[120px] rounded-md border overflow-hidden bg-white transition-all ${picked ? "border-brand ring-2 ring-brand/40" : "border-border hover:border-brand"} ${readOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                        >
                          <img src={`${IMG_BASE}${sp.photo_url}`} alt="" loading="lazy" className="w-full h-full object-contain" />
                          <span className="absolute top-0 left-0 text-[9px] px-1 rounded-br bg-brand/85 text-white">
                            {t(`quote.editor.src.${sp.source}`)}
                          </span>
                          <span className="absolute inset-x-0 bottom-0 bg-black/55 text-white text-[9px] font-mono truncate px-1">
                            {sp.item_number || sp.description}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </DialogContent>
          </Dialog>

          {/* Text fields */}
          <div className="space-y-2">
            {/* 2026-02-27 · 采购产品编号 (buyer_item_number) + 采购员 (buyer)
                exposed here per user spec — surfaced next to the standard
                description block so quote editors can populate the same
                buyer-facing linkage fields from within the quote.
                • buyer_item_number: placeholder shows the item_number so the
                  user knows "empty = fall back to item_number" without
                  wasting a keystroke to duplicate it.
                • buyer: dropdown of the platform-configured 采购员 list, plus
                  the current draft value as a first item if it happens to
                  be off-list (legacy data preservation). */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {/* 2026-06 · 产品编号 —— 外销/销售主管/管理员可在报价单内改（产品库不变） */}
              <div className="space-y-1">
                <Label className="text-xs">{t("pq.itemNumber")}</Label>
                <Input value={itemNumberInput}
                  readOnly={readOnly}
                  onChange={(e) => setItemNumberInput(e.target.value)}
                  onBlur={(e) => commitItemNumber(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                  data-testid="quote-editor-item-number"
                  className="text-sm font-mono font-semibold" />
                {draft.source_item_number && draft.source_item_number !== draft.item_number && (
                  <p className="text-[10px] font-mono text-muted-foreground" data-testid="quote-editor-item-number-source">
                    {t("quote.editor.originalItemNumber")}: {draft.source_item_number}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t("products.buyerItemNumber") || "采购产品编号"}</Label>
                <Input value={draft.buyer_item_number || ""}
                  placeholder={draft.item_number || ""}
                  readOnly={readOnly}
                  onChange={(e) => setItemField("buyer_item_number", e.target.value)}
                  data-testid="quote-editor-buyer-item-number"
                  className="text-sm font-mono" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t("products.buyer") || "采购"}</Label>
                {/* 2026-06 · 采购 select shrunk to 40% width + 是否询价 toggle:
                    only items flagged 询价 show up in that 采购's quote list. */}
                <div className="flex items-center gap-2">
                  <Select
                    value={draft.buyer || ""}
                    onValueChange={(v) => setItemField("buyer", v === "__none__" ? "" : v)}
                    disabled={readOnly}
                  >
                    <SelectTrigger data-testid="quote-editor-buyer" className="text-sm h-9 w-[40%] min-w-[92px]">
                      <SelectValue placeholder={t("quote.editor.buyerPlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {/* Preserve legacy off-list value so it doesn't get lost. */}
                      {draft.buyer && !buyerOptions.includes(draft.buyer) && (
                        <SelectItem value={draft.buyer}>{draft.buyer}</SelectItem>
                      )}
                      {buyerOptions.map((b) => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <label className={`flex items-center gap-1.5 text-xs whitespace-nowrap select-none ${readOnly ? "opacity-60" : "cursor-pointer"}`}>
                    <input
                      type="checkbox"
                      checked={Boolean(draft.inquire)}
                      disabled={readOnly}
                      onChange={(e) => {
                        setItemField("inquire", e.target.checked);
                        if (e.target.checked) setItemField("buyer_quoted_at", null);
                      }}
                      data-testid="quote-editor-inquire"
                      className="h-4 w-4 accent-[var(--brand,#2563eb)] cursor-pointer"
                    />
                    {t("quote.editor.inquire")}
                  </label>
                </div>
                {/* 2026-06 · Green note once the 采购 clicked 完成报价; it clears
                    as soon as 是否询价 is ticked again (user spec). */}
                {draft.buyer_quoted_at && !draft.inquire && (
                  <p className="text-[11px] text-emerald-600 font-medium" data-testid="quote-editor-buyer-quoted">
                    {t("quote.editor.buyerQuoted")}
                  </p>
                )}
              </div>
              {/* 2026-06 · 起订量 (MOQ) — free text, seeded from the product. */}
              <div className="space-y-1">
                <Label className="text-xs">{t("products.moq")}</Label>
                <Input
                  value={draft.moq || ""}
                  readOnly={readOnly}
                  placeholder={t("quote.editor.moqPlaceholder")}
                  onChange={(e) => setItemField("moq", e.target.value)}
                  data-testid="quote-editor-moq"
                  className="text-sm h-9" />
              </div>
              {/* 2026-06 · 库存数量 — 报价单内可单独修改，不影响产品库 */}
              <div className="space-y-1">
                <Label className="text-xs">{t("products.stockQuantity")}</Label>
                <Input
                  value={draft.stock_quantity || ""}
                  readOnly={readOnly}
                  placeholder={t("quote.editor.stockPlaceholder")}
                  onChange={(e) => setItemField("stock_quantity", e.target.value)}
                  data-testid="quote-editor-stock-quantity"
                  className="text-sm h-9" />
              </div>
            </div>
            {/* 2026-09 · 两列排布 —— 让整个窗体在常见屏幕上不需要垂直滚动 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">{t("quote.editor.description")}</Label>
              <Textarea rows={2} value={draft.description || ""}
                readOnly={readOnly}
                onChange={(e) => setItemField("description", e.target.value)}
                data-testid="quote-editor-description" className="text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("quote.editor.gtDescription")}</Label>
              <Input value={draft.gt_description || ""}
                readOnly={readOnly}
                onChange={(e) => setItemField("gt_description", e.target.value)}
                data-testid="quote-editor-gt-description" className="text-sm" />
              {(draft.gt_description || "").length > 30 && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400" data-testid="quote-editor-gt-length-warning">
                  {t("quote.editor.gtLengthWarning").replace("{n}", (draft.gt_description || "").length)}
                </p>
              )}
              {aiBusy && !(draft.gt_description || "") && (
                <p className="text-[10px] text-muted-foreground italic">{t("quote.editor.gtGenerating")}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("quote.editor.specMaterial")}</Label>
              <Textarea rows={2}
                /* 2026-06 · Show 规格 only; fall back to 材料 when 规格 is empty
                   (user spec — never both stacked on two lines). */
                value={draft.specification || draft.material || ""}
                readOnly={readOnly}
                onChange={(e) => {
                  // Store the whole combined text in `specification` and clear the legacy
                  // `material` field. The Quote list view will pick it up from either
                  // field (see saveItemField merge), so downstream stays consistent.
                  setItemField("specification", e.target.value);
                  setItemField("material", "");
                }}
                data-testid="quote-editor-spec-material" className="text-sm font-mono" />
            </div>
            {/* 2026-06 · 采购报价要求 — brief for the 采购, shared via 生成链接. */}
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs">{t("quote.editor.procurementNote")}</Label>
                {!readOnly && (
                  <Button type="button" size="sm" variant="outline"
                    onClick={generateProcurementLink}
                    disabled={linkBusy}
                    data-testid="quote-editor-generate-link"
                    className="h-7 gap-1 text-xs rounded-md">
                    <LinkIcon size={12} /> {linkBusy ? "…" : t("quote.editor.generateLink")}
                  </Button>
                )}
              </div>
              <Textarea rows={2}
                value={draft.procurement_note || ""}
                readOnly={readOnly}
                onChange={(e) => setItemField("procurement_note", e.target.value)}
                placeholder={t("quote.editor.procurementNotePlaceholder")}
                data-testid="quote-editor-procurement-note" className="text-sm font-mono" />
            </div>
            </div>
          </div>
        </section>

        {/* Price rows */}
        <section className="space-y-2 pt-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Label className="text-xs">{t("quote.editor.priceRows")}</Label>
            {/* 2026-02-09 · Two per-quote buttons requested by user:
                - 采购报价记录: opens the shared history dialog scoped to this
                  item, imports selected rows AS quote snapshots (never mutates
                  the source product).
                - 添加尺寸: appends a blank row via POST for filling manually. */}
            {!readOnly && ["admin", "export", "gt_export", "buyer", "sales_manager"].includes(user?.role) && (
              <div className="flex items-center gap-2">
                {/* 2026-09 · 关税 (CAD 报价) + 压缩率 — 放在 UPC 查询按钮左边 (user spec)。 */}
                {isCad && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{t("products.duty")}</span>
                    <input
                      type="number" step="0.001"
                      value={draft.duty_override ?? ""}
                      placeholder={String(draft.duty ?? "")}
                      onChange={(e) => setItemField("duty_override", e.target.value === "" ? "" : e.target.value)}
                      data-testid="quote-editor-duty"
                      className="h-7 w-16 rounded-md border border-border bg-background px-1.5 text-xs font-mono text-right outline-none focus:border-brand no-spinner"
                    />
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{t("priceList.compressionRate")}</span>
                  <input
                    type="number" step="0.01" placeholder="1.3"
                    value={draft.compression_rate ?? 0}
                    onChange={(e) => setItemField("compression_rate", e.target.value)}
                    onBlur={recalcNow}
                    data-testid="quote-editor-compression-rate"
                    className="h-7 w-16 rounded-md border border-border bg-background px-1.5 text-xs font-mono text-right outline-none focus:border-brand no-spinner"
                  />
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setUpcOpen(true)}
                  data-testid="quote-editor-open-upc"
                  className="h-7 gap-1 text-xs"
                >
                  <Barcode size={13} /> {t("upc.button")}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setProcOpen(true)}
                  disabled={appending}
                  data-testid="quote-editor-open-procurement"
                  className="h-7 gap-1 text-xs"
                >
                  <ClipboardList size={13} /> {t("pq.historyButton") || "采购报价记录"}
                </Button>
                {/* 2026-09 · 复制产品 — quote-only duplicate incl. price rows */}
                {["admin", "export"].includes(user?.role) && !readOnly && (
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={duplicateItem}
                    disabled={duplicating}
                    data-testid="quote-editor-duplicate-item"
                    className="h-7 gap-1 text-xs"
                  >
                    <Copy size={13} /> {duplicating ? "..." : (t("products.copyProduct") || "复制产品")}
                  </Button>
                )}
                {/* 2026-09 · 写回产品资料 (admin only, user spec) — 价格 + 压缩率 + 关税，不含 Final Price */}
                {user?.role === "admin" && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={writeBackToProduct}
                    disabled={writingBack}
                    data-testid="quote-editor-write-back"
                    className="h-7 gap-1 text-xs"
                  >
                    <Upload size={13} /> {writingBack ? "..." : (t("quote.editor.writeBack") || "写回产品资料")}
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  onClick={addBlankRow}
                  disabled={appending}
                  data-testid="quote-editor-add-size"
                  className="h-7 gap-1 text-xs bg-brand hover:bg-brand-hover text-white"
                >
                  <Plus size={13} /> {t("priceList.addSize") || "添加尺寸"}
                </Button>
              </div>
            )}
          </div>
          <div className="w-full min-w-0 rounded-md border border-border">
            <table className="w-full text-[10px] border-collapse" style={{ tableLayout: "fixed" }}>
              <thead className="bg-secondary/40">
                <tr>
                  {!readOnly && <th className="w-6" aria-label="drag" />}
                  {[
                    ["size", "15%"],
                    ["upc", "11%"],
                    ["customer_price", "8%"],
                    ["final_price", "8%"],
                    ["factory_cost", "12%"],
                    ["container_qty_40", "8%"],
                    ["container_qty_40_compressed", "8%"],
                    ["case_length", "6%"],
                    ["case_width", "6%"],
                    ["case_height", "6%"],
                    ["case_weight", "6%"],
                    ["qty_per_ctn", "6%"],
                  ].map(([k, w]) => (
                    <th key={k} style={{ width: w }}
                      className="border border-border px-1 py-1 text-center font-mono uppercase text-[8px] leading-tight text-muted-foreground break-words">
                      {t(`quote.editor.col.${k}`)}
                    </th>
                  ))}
                  {/* 2026-02-09 · Delete + 2026-06 导入订单产品资料 column. */}
                  {!readOnly && <th className="w-14" aria-label="row-actions" />}
                </tr>
              </thead>
              <tbody>
                {(draft.price_rows || []).map((r) => {
                  // Merged currency-aware factory-cost column: show whichever value is populated.
                  // Priority: cost_currency snapshot > non-zero value > USD default. Toggling the
                  // currency select MOVES the value across factory_cost_usd ↔ factory_cost so we
                  // never end up with two conflicting numbers.
                  const activeCur = (r.cost_currency || "").toUpperCase() === "CNY"
                    || (r.cost_currency || "").toUpperCase() === "RMB"
                    || ((r.factory_cost || 0) > 0 && !(r.factory_cost_usd || 0))
                    ? "CNY" : "USD";
                  const activeValue = activeCur === "USD" ? (r.factory_cost_usd ?? 0) : (r.factory_cost ?? 0);
                  const switchCurrency = (newCur) => {
                    if (newCur === activeCur) return;
                    // Move the current value into the new-currency slot, zero out the old one.
                    if (newCur === "USD") {
                      setRowField(r.id, "factory_cost_usd", activeValue);
                      setRowField(r.id, "factory_cost", 0);
                    } else {
                      setRowField(r.id, "factory_cost", activeValue);
                      setRowField(r.id, "factory_cost_usd", 0);
                    }
                    setRowField(r.id, "cost_currency", newCur);
                  };
                  const setFactoryCost = (v) => {
                    setRowField(r.id, activeCur === "USD" ? "factory_cost_usd" : "factory_cost", v);
                    if (!r.cost_currency) setRowField(r.id, "cost_currency", activeCur);
                  };
                  return (
                  <tr key={r.id}
                    data-editor-row-id={r.id}
                    className={`border-t border-border/60 transition-colors ${dragId === r.id ? "opacity-40" : ""} ${overId === r.id && dragId !== r.id ? "bg-brand/10" : ""}`}
                    data-testid={`quote-editor-row-${r.id}`}>
                    {!readOnly && (
                      <td className="border border-border p-0 w-5">
                        <span
                          onPointerDown={startRowDrag(r.id)}
                          title={t("quote.editor.dragToSort") || "拖动排序"}
                          data-testid={`quote-editor-row-drag-${r.id}`}
                          className="flex items-center justify-center cursor-grab active:cursor-grabbing touch-none text-muted-foreground/60 hover:text-brand transition-colors"
                        >
                          <GripVertical size={13} />
                        </span>
                      </td>
                    )}
                    <td className="border border-border p-0">
                      <Input value={r.size ?? ""}
                        readOnly={readOnly}
                        onChange={(e) => setRowField(r.id, "size", e.target.value)}
                        data-testid={`quote-editor-row-size-${r.id}`}
                        className="h-6 w-full bg-transparent border-0 rounded-none px-1 text-[10px] md:text-[10px] font-mono font-semibold text-foreground focus-visible:ring-0 focus:bg-brand/5 no-spinner" />
                      {(r.source_buyer || r.imported_at) && (
                        <div className="px-1 pt-0.5 font-mono text-[9px] leading-tight text-muted-foreground whitespace-nowrap"
                          title={`${t("priceList.importedFrom")}${r.source_buyer || "—"}${r.imported_at ? ` · ${String(r.imported_at).slice(0, 10)}` : ""}`}
                          data-testid={`quote-editor-row-source-${r.id}`}>
                          {/* 2026-06 · 年份缩写成两位，保证日期完整显示不被截断（用户要求 26-09-17） */}
                          {[r.source_buyer, String(r.imported_at || "").slice(2, 10)].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </td>
                    <td className="border border-border p-0">
                      <Input value={r.upc ?? ""}
                        readOnly={readOnly}
                        onChange={(e) => setRowField(r.id, "upc", e.target.value)}
                        data-testid={`quote-editor-row-upc-${r.id}`}
                        className="h-6 w-full bg-transparent border-0 rounded-none px-1 text-[10px] md:text-[10px] font-mono font-semibold text-foreground focus-visible:ring-0 focus:bg-brand/5 no-spinner" />
                    </td>
                    {/* 2026-06 · 计算价 — read-only, computed from the current
                        settings + customer multiplier (user spec: show the
                        calculated price here, not the Final Price). */}
                    <td className="border border-border p-0">
                      <div className="h-6 px-1 flex items-center justify-end bg-secondary/40 text-[10px] md:text-[10px] font-mono font-bold text-brand"
                        data-testid={`quote-editor-row-customer_price-${r.id}`}>
                        {r.customer_price ? r.customer_price.toFixed(3) : "-"}
                      </div>
                    </td>
                    {/* 2026-09 · Final Price — 手工最终价，紧邻计算价 (user spec)。 */}
                    <td className="border border-border p-0">
                      <Input type="number" step="any"
                        value={r.final_price ?? 0}
                        readOnly={readOnly}
                        onChange={(e) => setRowField(r.id, "final_price", e.target.value)}
                        data-testid={`quote-editor-row-final_price-${r.id}`}
                        className="h-6 w-full bg-transparent border-0 rounded-none px-1 text-[13px] md:text-[13px] font-mono font-bold text-foreground text-right focus-visible:ring-0 focus:bg-brand/5 no-spinner" />
                    </td>
                    {/* Merged factory cost — pick currency, single value */}
                    <td className="border border-border p-0">
                      <div className="flex items-stretch gap-0.5">
                        <select
                          value={activeCur}
                          onChange={(e) => switchCurrency(e.target.value)}
                          disabled={readOnly}
                          data-testid={`quote-editor-row-factory_cost_currency-${r.id}`}
                          className={`h-6 rounded-none border-0 bg-transparent text-[9px] font-mono px-0.5 focus:outline-none ${activeCur === "USD" ? "text-emerald-600" : "text-rose-600"}`}
                        >
                          <option value="USD">US$</option>
                          <option value="CNY">¥</option>
                        </select>
                        <Input type="number" step="any" value={activeValue}
                          readOnly={readOnly}
                          onChange={(e) => setFactoryCost(e.target.value)}
                          data-testid={`quote-editor-row-factory_cost-${r.id}`}
                          className="h-6 flex-1 bg-transparent border-0 rounded-none px-1 text-[10px] md:text-[10px] font-mono font-semibold text-foreground text-right focus-visible:ring-0 focus:bg-brand/5 no-spinner" />
                      </div>
                    </td>
                    {/* remaining numeric cols (40ft container qty + 压缩率/压缩后柜量 + dims + weight + pack qty) */}
                    {["container_qty_40"].map((k) => (
                      <td key={k} className="border border-border p-0">
                        <Input type="number" step="any"
                          value={r[k] ?? 0}
                          readOnly={readOnly}
                          onChange={(e) => setRowField(r.id, k, e.target.value)}
                          data-testid={`quote-editor-row-${k}-${r.id}`}
                          className="h-6 w-full bg-transparent border-0 rounded-none px-1 text-[10px] md:text-[10px] font-mono font-semibold text-foreground text-right focus-visible:ring-0 focus:bg-brand/5 no-spinner" />
                      </td>
                    ))}
                    <td className="border border-border p-0">
                      <div className="h-6 px-1 flex items-center justify-end bg-secondary/40 text-[10px] md:text-[10px] font-mono font-semibold text-foreground"
                        data-testid={`quote-editor-row-container_qty_40_compressed-${r.id}`}>
                        {Math.round(r.container_qty_40_compressed || r.container_qty_40 || 0) || "-"}
                      </div>
                    </td>
                    {["case_length", "case_width", "case_height", "case_weight", "qty_per_ctn"].map((k) => (
                      <td key={k} className="border border-border p-0">
                        <Input type="number" step="any"
                          value={r[k] ?? 0}
                          readOnly={readOnly}
                          onChange={(e) => setRowField(r.id, k, e.target.value)}
                          data-testid={`quote-editor-row-${k}-${r.id}`}
                          className="h-6 w-full bg-transparent border-0 rounded-none px-1 text-[10px] md:text-[10px] font-mono font-semibold text-foreground text-right focus-visible:ring-0 focus:bg-brand/5 no-spinner" />
                      </td>
                    ))}
                    {/* 2026-02-09 · Per-row delete button — mirrors the 添加尺寸
                        button on the header. Calls DELETE /rows/{id}; if this
                        was the last row, backend also drops the item and we
                        close the dialog. */}
                    {!readOnly && (
                      <td className="border border-border p-0 w-12">
                        <div className="flex items-center gap-0.5">
                        <OrderProductImportButton
                          itemNumber={draft.item_number || ""}
                          iconOnly
                          variant="ghost"
                          className="h-6 w-6"
                          allowPriceImport
                          currentPrice={Number(r.factory_cost || 0)}
                          testId={`quote-editor-row-import-order-${r.id}`}
                          onPick={(rec, opts = {}) => {
                            Object.entries(toPriceRowCarton(rec)).forEach(([k, v]) => {
                              if (v) setRowField(r.id, k, v);
                            });
                            // 历史采购价是人民币 → 写入 factory_cost 并把该行切回 RMB
                            if (opts.withPrice && rec.purchase_price) {
                              setRowField(r.id, "factory_cost", rec.purchase_price);
                              setRowField(r.id, "factory_cost_usd", 0);
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => removeRow(r.id)}
                          disabled={appending}
                          data-testid={`quote-editor-row-delete-${r.id}`}
                          title={t("quote.editor.deleteRow") || "删除此尺寸"}
                          className="p-1 rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors disabled:opacity-40"
                        >
                          <Trash2 size={13} />
                        </button>
                        </div>
                      </td>
                    )}
                  </tr>
                  );
                })}
                {(!draft.price_rows || draft.price_rows.length === 0) && (
                  <tr>
                    <td colSpan={readOnly ? 12 : 14} className="px-2 py-6 text-center text-muted-foreground text-xs">
                      {t("quote.editor.noRows")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-border/40 mt-2">
          <Button variant="outline" onClick={handleClose} data-testid="quote-editor-close-button"
            disabled={saving} className="rounded-md gap-1">
            <Save size={14} /> {saving ? "…" : t("quote.editor.saveAndClose")}
          </Button>
        </div>
      </DialogContent>
      {/* 2026-02-09 · Procurement history importer, scoped to this quote item.
          On import creates snapshot rows only (never touches source product).
          2026-02-27 · Filter key = effective buyer_item_number (falls back to
          product item_number when empty), per user spec — opens the history
          filtered to procurement records whose 产品编号 matches this product's
          采购产品编号. */}
      {draft?.item_number && (
        <ProcurementHistoryDialog
          open={procOpen}
          onOpenChange={setProcOpen}
          buyerItemNumber={draft.buyer_item_number || draft.item_number}
          onImport={importProcurementRows}
        />
      )}
      {/* 2026-06 · UPC 查询 — FM UPC_API lookup + mint, writes into a price row. */}
      <UpcLookupDialog
        open={upcOpen}
        onClose={() => setUpcOpen(false)}
        itemNumber={draft?.item_number}
        description={draft?.description}
        rows={draft?.price_rows || []}
        onPick={(rowId, code) => setRowField(rowId, "upc", code)}
      />
      {/* 2026-06 · Generated 采购 link — shown after 生成链接 (auto-copied). */}
      <Dialog open={Boolean(linkInfo)} onOpenChange={(o) => { if (!o) setLinkInfo(null); }}>
        <DialogContent className="max-w-md" data-testid="procurement-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">{t("quote.editor.linkTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              {t("quote.editor.linkHint")
                .replace("{buyer}", linkInfo?.buyer || "")
                .replace("{n}", String(linkInfo?.item_count ?? 0))}
            </p>
            <div className="flex items-center gap-2">
              <Input readOnly value={linkInfo?.url || ""} data-testid="procurement-link-url"
                className="text-xs font-mono" onFocus={(e) => e.target.select()} />
              <Button type="button" variant="outline" size="sm" className="h-9 shrink-0"
                data-testid="procurement-link-copy"
                onClick={() => {
                  navigator.clipboard?.writeText(linkInfo?.url || "");
                  toast.success(t("quote.editor.linkCopied"));
                }}>
                {t("quote.editor.linkCopy")}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              {t("quote.editor.linkExpires").replace("{date}", (linkInfo?.expires_at || "").slice(0, 10))}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
